import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FaShoppingCart, FaLaptopCode, FaMobile, FaSearch, FaRocket, FaHeadset, FaWhatsapp } from 'react-icons/fa';
import Button from '../components/Button';
import { Link } from 'react-router-dom';
import BookAppointment from '../components/BookAppointment';

const ServicesSection = styled.section`
  padding: 5rem 0;
  background: #0a0a0a;
`;

const ServicesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 3rem;
  
  h2 {
    font-size: clamp(2rem, 4vw, 3rem);
    margin-bottom: 1rem;
    color: #ffffff;
  }
  
  p {
    font-size: 1.2rem;
    color: #cccccc;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`;

const ServiceCard = styled(motion.div)`
  background: #1a1a1a;
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  text-align: center;
  transition: all 0.3s ease;
  border: 2px solid rgba(70, 130, 180, 0.1);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
    border-color: rgba(70, 130, 180, 0.3);
  }
`;

const ServiceIcon = styled.div`
  font-size: 3.5rem;
  color: #4682B4;
  margin-bottom: 1.5rem;
  display: inline-block;
  padding: 1rem;
  background: rgba(70, 130, 180, 0.1);
  border-radius: 50%;
  transition: all 0.3s ease;
  
  ${ServiceCard}:hover & {
    background: linear-gradient(135deg, #4682B4 0%, #87CEEB 100%);
    color: #000000;
    transform: scale(1.1);
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #ffffff;
`;

const ServiceDescription = styled.p`
  color: #cccccc;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  text-align: left;
  margin-bottom: 1.5rem;
  
  li {
    color: #cccccc;
    margin-bottom: 0.5rem;
    padding-left: 1.5rem;
    position: relative;
    
    &::before {
      content: '✓';
      position: absolute;
      left: 0;
      color: #4682B4;
      font-weight: bold;
    }
  }
`;

const WhatsAppButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #25D366;
  color: white;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 500;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover {
    background: #128C7E;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(37, 211, 102, 0.3);
  }
  
  svg {
    font-size: 1.2rem;
  }
`;

const ProcessSection = styled.div`
  background: #1a1a1a;
  padding: 4rem 2rem;
  border-radius: 20px;
  margin-top: 4rem;
  border: 1px solid rgba(255, 215, 0, 0.1);
`;

const ProcessTitle = styled(motion.h2)`
  text-align: center;
  font-size: clamp(1.8rem, 4vw, 2.5rem);
  margin-bottom: 3rem;
  color: #ffffff;
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const ProcessStep = styled(motion.div)`
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 2rem;
    right: -20%;
    width: 40%;
    height: 2px;
    background: linear-gradient(90deg, #667eea, transparent);
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  &:last-child::after {
    display: none;
  }
`;

const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #4682B4 0%, #87CEEB 100%);
  color: #000000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 auto 1rem;
`;

const StepTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  color: #ffffff;
`;

const StepDescription = styled.p`
  color: #cccccc;
  line-height: 1.5;
`;

const CTASection = styled(motion.div)`
  text-align: center;
  padding: 3rem;
  background: linear-gradient(135deg, #4682B4 0%, #87CEEB 100%);
  border-radius: 20px;
  margin-top: 3rem;
  color: #000000;
  
  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    opacity: 0.9;
  }
`;

const Services = () => {
  const services = [
    {
      icon: <FaShoppingCart style={{ color: '#25D366' }} />,
      title: "E-commerce Development",
      description: "Complete online store solutions with shopping carts, payment integration, and inventory management.",
      features: [
        "Custom shopping cart design",
        "Secure payment gateway integration",
        "Inventory management system",
        "Order tracking and management",
        "Mobile-responsive design",
        "SEO optimization"
      ]
    },
    {
      icon: <FaLaptopCode style={{ color: '#DC143C' }} />,
      title: "Business Websites",
      description: "Professional business websites that establish your online presence and attract customers.",
      features: [
        "Modern, responsive design",
        "Content management systems",
        "Contact forms and lead capture",
        "Blog and news sections",
        "Social media integration",
        "Analytics and reporting"
      ]
    },
    {
      icon: <FaRocket />,
      title: "Custom Web Solutions",
      description: "Tailored web applications built to meet your specific business requirements and goals.",
      features: [
        "Custom functionality development",
        "Database design and integration",
        "API development and integration",
        "Real-time features",
        "Scalable architecture",
        "Performance optimization"
      ]
    },
    {
      icon: <FaMobile />,
      title: "Mobile App Development",
      description: "Native and cross-platform mobile applications for iOS and Android devices.",
      features: [
        "React Native development",
        "Cross-platform compatibility",
        "Offline functionality",
        "Push notifications",
        "App store deployment",
        "Performance optimization"
      ]
    },
    {
      icon: <FaSearch style={{ color: '#FFD700' }} />,
      title: "SEO & Digital Marketing",
      description: "Comprehensive SEO strategies and digital marketing solutions to boost your online visibility.",
      features: [
        "On-page SEO optimization",
        "Technical SEO audits",
        "Content strategy development",
        "Performance monitoring",
        "Local SEO optimization",
        "Analytics and reporting"
      ]
    },
    {
      icon: <FaHeadset />,
      title: "Website Maintenance",
      description: "Ongoing support and maintenance to keep your website running smoothly and securely.",
      features: [
        "Regular updates and backups",
        "Security monitoring",
        "Performance optimization",
        "Bug fixes and troubleshooting",
        "Content updates",
        "24/7 technical support"
      ]
    }
  ];

  const processSteps = [
    {
      number: "01",
      title: "Discovery & Planning",
      description: "Understanding your requirements and creating a detailed project plan."
    },
    {
      number: "02",
      title: "Design & Development",
      description: "Creating mockups and developing the solution with modern technologies."
    },
    {
      number: "03",
      title: "Testing & Quality Assurance",
      description: "Rigorous testing to ensure everything works perfectly across all devices."
    },
    {
      number: "04",
      title: "Launch & Support",
      description: "Deploying your project and providing ongoing support and maintenance."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  return (
    <ServicesSection>
      <ServicesContainer>
        <SectionHeader
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2>My Services</h2>
          <p>
            I offer comprehensive web development services tailored to help your business 
            succeed online. From e-commerce solutions to custom web applications, I deliver 
            high-quality results that exceed expectations.
          </p>
        </SectionHeader>

        <ServicesGrid
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
            >
              <ServiceIcon>{service.icon}</ServiceIcon>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDescription>{service.description}</ServiceDescription>
              <ServiceFeatures>
                {service.features.map((feature, featureIndex) => (
                  <li key={featureIndex}>{feature}</li>
                ))}
              </ServiceFeatures>
              <WhatsAppButton 
                href="https://wa.me/923095089454?text=Hi!%20I'm%20interested%20in%20your%20${encodeURIComponent(service.title)}%20service"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaWhatsapp />
                Chat on WhatsApp
              </WhatsAppButton>
            </ServiceCard>
          ))}
        </ServicesGrid>

        <ProcessSection>
          <ProcessTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            My Development Process
          </ProcessTitle>
          <ProcessSteps>
            {processSteps.map((step, index) => (
              <ProcessStep
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <StepNumber>{step.number}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </ProcessStep>
            ))}
          </ProcessSteps>
        </ProcessSection>

        <div style={{ marginTop: '4rem' }}>
          <BookAppointment />
        </div>
      </ServicesContainer>
    </ServicesSection>
  );
};

export default Services;
